import React from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchFilters from '../../../reportPage/middleContainer/sections/search/components/SearchFilters';

/** Small count badge shown inside a toggle button when search results are available. */
const ScopeCountBadge = ({count, loading}) => {
    if (loading) {
        return (
            <CircularProgress
                size={8}
                thickness={5}
                sx={{ml: 0.5, opacity: 0.6}}
                aria-label="counting"
            />
        );
    }
    if (count == null) return null;
    return (
        <Box
            component="span"
            sx={{
                ml: 0.5,
                px: 0.5,
                py: 0,
                borderRadius: '8px',
                bgcolor: 'action.selected',
                fontSize: '0.65rem',
                fontWeight: 700,
                lineHeight: '14px',
                minWidth: '14px',
                textAlign: 'center',
                display: 'inline-block',
            }}
        >
            {count > 99 ? '99+' : count}
        </Box>
    );
};

/** Build the tooltip content showing docs + segments for a scope. */
function buildCountTooltip(baseTitle, scopeKey, scopeCounts) {
    if (!scopeCounts || !scopeCounts[scopeKey]) return baseTitle;
    const {docs, segments} = scopeCounts[scopeKey];
    return `${baseTitle} · ${docs} doc${docs !== 1 ? 's' : ''}, ${segments} segment${segments !== 1 ? 's' : ''}`;
}

const SourcesSectionSearchHeader = ({
    inputValue,
    onInputChange,
    showFilters,
    onToggleFilters,
    sourceScope = 'both',
    onSourceScopeChange,
    searchMode = 'keyword',
    onSearchModeChange,
    passageContext = '',
    onPassageContextChange,
    scopeCounts = null,
    isScopeCounting = false,
}) => {
    const handleScopeChange = React.useCallback((_event, next) => {
        if (next && onSourceScopeChange) {
            onSourceScopeChange(next);
        }
    }, [onSourceScopeChange]);

    const handleAiToggle = React.useCallback(() => {
        if (onSearchModeChange) {
            onSearchModeChange(searchMode === 'hybrid' ? 'keyword' : 'hybrid');
        }
    }, [onSearchModeChange, searchMode]);

    const isAiMode = searchMode === 'hybrid';
    const hasQuery = Boolean(inputValue && inputValue.trim());

    const getCount = (scopeKey) => {
        if (!hasQuery || !scopeCounts) return null;
        return scopeCounts[scopeKey]?.segments ?? null;
    };

    return (
        <Stack p={2} bgcolor="white" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                    value={inputValue}
                    onChange={onInputChange}
                    label="Search sources"
                    variant="outlined"
                    size="small"
                    sx={{flexGrow: 1}}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon fontSize="small" color="action"/>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                <Tooltip title={isAiMode ? 'AI search on — click for Hybrid (default)' : 'Hybrid search (default) — click for AI search'}>
                    <IconButton
                        aria-label="toggle ai search"
                        aria-pressed={isAiMode}
                        onClick={handleAiToggle}
                        size="small"
                        color={isAiMode ? 'primary' : 'default'}
                    >
                        <AutoAwesomeIcon/>
                    </IconButton>
                </Tooltip>

                <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
                    <IconButton
                        aria-label="toggle filters"
                        onClick={onToggleFilters}
                        size="small"
                        color={showFilters ? 'primary' : 'default'}
                    >
                        <FilterListIcon/>
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack direction="row" alignItems="center">
                <ToggleButtonGroup
                    value={sourceScope}
                    exclusive
                    onChange={handleScopeChange}
                    size="small"
                    aria-label="source type"
                    fullWidth
                    sx={{
                        width: '100%',
                        '& .MuiToggleButton-root': {
                            flex: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            px: 0.75,
                            fontSize: '0.75rem',
                        },
                    }}
                >
                    <Tooltip title={buildCountTooltip('Search across all source types', 'all', scopeCounts)} placement="top" arrow>
                        <ToggleButton value="all" aria-label="all stores">
                            <Typography component="span" variant="inherit" sx={{fontSize: 'inherit', fontWeight: 'inherit'}}>All</Typography>
                            <ScopeCountBadge count={getCount('all')} loading={isScopeCounting && hasQuery && !scopeCounts}/>
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title={buildCountTooltip('Files: My Files, Shared, and Collected Materials', 'data_items', scopeCounts)} placement="top" arrow>
                        <ToggleButton
                            value="data_items"
                            aria-label="files only"
                            sx={{
                                '&&.Mui-selected': {
                                    color: 'success.contrastText',
                                    backgroundColor: 'success.main',
                                    '&:hover': {backgroundColor: 'success.dark'},
                                },
                            }}
                        >
                            <Typography component="span" variant="inherit" sx={{fontSize: 'inherit', fontWeight: 'inherit'}}>Files</Typography>
                            <ScopeCountBadge count={getCount('data_items')} loading={isScopeCounting && hasQuery && !scopeCounts}/>
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title={buildCountTooltip('Approved products (analytic, SITREP, pitch, IIR, …)', 'reports', scopeCounts)} placement="top" arrow>
                        <ToggleButton
                            value="reports"
                            aria-label="reports only"
                            sx={{
                                '&&.Mui-selected': {
                                    color: 'warning.contrastText',
                                    backgroundColor: 'warning.main',
                                    '&:hover': {backgroundColor: 'warning.dark'},
                                },
                            }}
                        >
                            <Typography component="span" variant="inherit" sx={{fontSize: 'inherit', fontWeight: 'inherit'}}>Products</Typography>
                            <ScopeCountBadge count={getCount('reports')} loading={isScopeCounting && hasQuery && !scopeCounts}/>
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title={buildCountTooltip('News feed and OSINT sources (external intelligence data)', 'corpus', scopeCounts)} placement="top" arrow>
                        <ToggleButton
                            value="corpus"
                            aria-label="corpus only"
                            sx={{
                                '&&.Mui-selected': {
                                    color: 'primary.contrastText',
                                    backgroundColor: 'primary.main',
                                    '&:hover': {backgroundColor: 'primary.dark'},
                                },
                            }}
                        >
                            <Typography component="span" variant="inherit" sx={{fontSize: 'inherit', fontWeight: 'inherit'}}>Corpus</Typography>
                            <ScopeCountBadge count={getCount('corpus')} loading={isScopeCounting && hasQuery && !scopeCounts}/>
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip title={buildCountTooltip('Passages you have highlighted across documents', 'highlights', scopeCounts)} placement="top" arrow>
                        <ToggleButton
                            value="highlights"
                            aria-label="highlights only"
                            sx={{
                                '&&.Mui-selected': {
                                    color: 'secondary.contrastText',
                                    backgroundColor: 'secondary.main',
                                    '&:hover': {backgroundColor: 'secondary.dark'},
                                },
                            }}
                        >
                            <Typography component="span" variant="inherit" sx={{fontSize: 'inherit', fontWeight: 'inherit'}}>Highlights</Typography>
                            <ScopeCountBadge count={getCount('highlights')} loading={isScopeCounting && hasQuery && !scopeCounts}/>
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </Stack>

            <TextField
                value={passageContext}
                onChange={onPassageContextChange}
                label="Passage context (optional)"
                placeholder="Add surrounding context to sharpen AI ranking"
                variant="outlined"
                size="small"
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
            />

            <SearchFilters sourceScope={sourceScope}/>
        </Stack>
    );
};

export default React.memo(SourcesSectionSearchHeader);
